import api from './api';
import { AxiosResponse } from 'axios';

export interface PropertyStat {
  property_id: string;
  property_name: string;
  check_ins: number;
  check_outs: number;
}

export interface DashboardStats {
  check_ins: number;
  check_outs: number;
  in_house: number;
  cancellations: number;
  property_stats: PropertyStat[];
}

export interface PropertyOccupancy {
  name: string;
  check_ins: number;
}

export interface MarketSegment {
  name: string;
  count: number;
  percentage: number;
}

export interface Reservation {
  id: number;
  crs_no: string;
  guest_name: string;
  guest_city: string | null;
  arrival_date: string | null;
  departure_date: string | null;
  property: string | null;
  property_id: string | null;
  property_name: string | null;
  room_type: string | null;
  reservation_status: string;
  booked_by: string | null;
  check_in_time: string | null;
  check_out_time: string | null;
}

export interface ReservationDetail extends Reservation {
  email: string | null;
  phone: string | null;
  address: string | null;
  country: string | null;
  adults: number;
  children: number;
  special_requests: string | null;
  payment_status: string;
  total_amount: number;
  paid_amount: number;
  remaining_amount: number;
  room_details: {
    name: string;
    type: string;
    rate: number;
    guests: number;
  }[];
  notes: {
    date: string;
    text: string;
    added_by: string;
  }[];
  timeline: {
    date: string;
    event: string;
    user: string;
  }[];
}

const dashboardService = {
  getStats: async (startDate?: string, endDate?: string): Promise<DashboardStats> => {
    try {
      const params: Record<string, string> = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response: AxiosResponse = await api.get('/dashboard/stats/', { params });
      if (!response.data.data) {
        throw new Error('Invalid response format from API');
      }
      return response.data.data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  },

  getPropertyOccupancy: async (
    startDate?: string,
    endDate?: string,
    month?: number,
    year?: number
  ): Promise<PropertyOccupancy[]> => {
    try {
      const params: Record<string, string | number> = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (month) params.month = month;
      if (year) params.year = year;
      // Always send custom period when dates are provided
      params.period = startDate && endDate ? 'custom' : 'monthly';

      const response: AxiosResponse = await api.get('/dashboard/property-occupancy/', { params });
      if (!response.data.data?.properties) {
        throw new Error('Invalid property occupancy response format from API');
      }
      return response.data.data.properties;
    } catch (error) {
      console.error('Error fetching property occupancy:', error);
      throw error;
    }
  },

  getMarketSegmentation: async (
    startDate?: string,
    endDate?: string,
    month?: number,
    year?: number
  ): Promise<MarketSegment[]> => {
    try {
      const params: Record<string, string | number> = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (month) params.month = month;
      if (year) params.year = year;
      params.period = startDate && endDate ? 'custom' : 'monthly';

      const response: AxiosResponse = await api.get('/dashboard/market-segmentation/', { params });
      if (!response.data.data?.segments) {
        throw new Error('Invalid market segmentation response format from API');
      }
      return response.data.data.segments;
    } catch (error) {
      console.error('Error fetching market segmentation:', error);
      throw error;
    }
  },

  getReservationCities: async (): Promise<string[]> => {
    try {
      const response: AxiosResponse = await api.get('/dashboard/reservation-cities/');
      if (!response.data.data?.cities) {
        throw new Error('Invalid reservation cities response format from API');
      }
      return response.data.data.cities;
    } catch (error) {
      console.error('Error fetching reservation cities:', error);
      throw error;
    }
  },

  getReservations: async (
    status?: string,
    propertyId?: string,
    limit: number = 10,
    search?: string,
    offset: number = 0,
    startDate?: string,
    endDate?: string,
    city?: string
  ): Promise<{ reservations: Reservation[]; total_count: number }> => {
    try {
      const params: Record<string, string | number> = { limit, offset };
      if (status) params.status = status;
      if (propertyId) params.property_id = propertyId;
      if (city) params.city = city;
      if (search) params.search = search;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response: AxiosResponse = await api.get('/dashboard/reservations/', { params });
      if (!response.data.data) {
        throw new Error('Invalid reservations response format from API');
      }
      return response.data.data;
    } catch (error) {
      console.error('Error fetching reservations:', error);
      throw error;
    }
  },

  exportReservationsCsv: async (options: {
    status?: string;
    propertyId?: string;
    city?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<void> => {
    const params: Record<string, string> = { format: 'csv' };
    if (options.status) params.status = options.status;
    if (options.propertyId) params.property_id = options.propertyId;
    if (options.city) params.city = options.city;
    if (options.search) params.search = options.search;
    if (options.startDate) params.start_date = options.startDate;
    if (options.endDate) params.end_date = options.endDate;

    const response: AxiosResponse<Blob> = await api.get('/dashboard/reservations/', {
      params,
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const cd = response.headers['content-disposition'] as string | undefined;
    let filename = 'reservations_export.csv';
    if (cd) {
      const m = /filename="?([^";\n]+)"?/i.exec(cd);
      if (m?.[1]) filename = m[1].trim();
    }
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  getReservationDetail: async (reservationId: number): Promise<ReservationDetail> => {
    try {
      const response: AxiosResponse = await api.get(`/dashboard/reservations/${reservationId}/`);
      if (!response.data.data) {
        throw new Error('Invalid reservation detail response format from API');
      }
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching reservation details for ID ${reservationId}:`, error);
      throw error;
    }
  }
};

export default dashboardService;
